import React, { useEffect, useState } from 'react'
import { Layout as AntLayout, Menu, Button, Badge, theme, Drawer } from 'antd'
import {
  DashboardOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuOutlined,
} from '@ant-design/icons'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@stores/authStore'
import { useSocketStore } from '@stores/socketStore'
import { useWebSocket } from '@hooks/useWebSocket'
import styles from './index.module.css'

const { Header, Sider, Content } = AntLayout

const Layout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { token, user, logout } = useAuthStore()
  const { connect, disconnect, isConnected } = useSocketStore()
  const { token: { colorBgContainer } } = theme.useToken()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // 启用 WebSocket 监听
  useWebSocket()

  useEffect(() => {
    if (token) {
      connect(token)
    }
    return () => {
      disconnect()
    }
  }, [token, connect, disconnect])

  // 监听窗口大小变化
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleLogout = () => {
    logout()
    localStorage.removeItem('authToken')
    navigate('/login')
  }

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: '仪表板',
    },
    {
      key: '/devices',
      icon: <UnorderedListOutlined />,
      label: '设备列表',
    },
    {
      key: '/ota',
      icon: <SettingOutlined />,
      label: 'OTA 升级',
    },
  ]

  const handleMenuClick = (key: string) => {
    navigate(key)
    setMobileMenuOpen(false)
  }

  return (
    <AntLayout className={styles.layout}>
      <Header className={styles.header} style={{ background: colorBgContainer }}>
        <div className={styles.headerLeft}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuOpen(true)}
              className={styles.menuButton}
            />
          )}
          <div className={styles.logo}>
            <span>DTU 设备管理系统</span>
          </div>
        </div>
        <div className={styles.headerRight}>
          <Badge 
            status={isConnected ? 'success' : 'error'} 
            text={isConnected ? '实时连接' : '离线'}
            className={styles.connectionBadge}
          />
          <span className={styles.username}>{user?.username || '管理员'}</span>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            className={styles.logoutButton}
          >
            {!isMobile && '退出'}
          </Button>
        </div>
      </Header>
      <AntLayout>
        {!isMobile && (
          <Sider width={200} style={{ background: colorBgContainer }}>
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={({ key }) => navigate(key)}
              style={{ height: '100%', borderRight: 0 }}
            />
          </Sider>
        )}
        <Content className={styles.content}>
          <Outlet />
        </Content>
      </AntLayout>
      
      {/* 移动端抽屉式导航 */}
      <Drawer
        title="菜单"
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        className={styles.mobileDrawer}
      >
        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => handleMenuClick(key)}
          style={{ borderRight: 0 }}
        />
      </Drawer>
    </AntLayout>
  )
}

export default Layout
